import React, { useState, useEffect, useContext } from 'react';
import useAnalytics from '../../analytics/useAnalytics';
import { EVT_INSTITUTE_LEAD_SUBMIT } from '../../analytics/events';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  Skeleton,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Avatar,
  Paper,
  Snackbar,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import {
  LocationOn,
  ArrowBack,
  Share,
  Star,
  TrendingUp,
  Groups,
  Phone,
  Email,
  Language,
  Check,
  YouTube,
  Close,
  Business,
  Place,
  Verified,
  Category,
  WhatsApp,
  FavoriteBorder,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import BottomSheet from '../../components/shared/BottomSheet';
import { RADIUS } from '../../theme/tokens';
import StudentBottomNav from '../../components/StudentBottomNav';
import DesktopNav from '../../components/DesktopNav';
import HeroCarousel from './instituteDetail/HeroCarousel';
import Lightbox from './instituteDetail/Lightbox';
import PlanShelf from './instituteDetail/PlanShelf';
import BatchShelf from './instituteDetail/BatchShelf';

const MotionCard = motion(Card);

// ── Classroom-themed fallback images (all verified 200) ─────────────────────
const FALLBACK_IMAGES = [
  'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=1200&h=400&fit=crop',
  'https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=1200&h=400&fit=crop',
  'https://images.unsplash.com/photo-1571260899304-425eee4c7efc?w=1200&h=400&fit=crop',
];

// ── confetti burst (no new lib) ──────────────────────────────────────────────
function ConfettiBurst({ active }) {
  const theme = useTheme();
  const palette = [
    theme.palette.primary.main,
    theme.palette.secondary?.main ?? theme.palette.primary.light,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
  ];

  if (!active) return null;
  return (
    <Box
      sx={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        pointerEvents: 'none',
        zIndex: 9999,
      }}
    >
      {Array.from({ length: 30 }).map((_, i) => {
        const x = (Math.random() - 0.5) * 400;
        const y = (Math.random() - 0.5) * 400;
        const rotate = Math.random() * 720 - 360;
        const size = 8 + Math.random() * 8;
        const color = palette[Math.floor(Math.random() * palette.length)];
        const delay = Math.random() * 0.3;
        return (
          <motion.div
            key={i}
            initial={{ opacity: 1, y: 0, x: 0, rotate: 0, scale: 1 }}
            animate={{ opacity: 0, y, x, rotate, scale: 0.2 }}
            transition={{ duration: 1.2, delay, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              width: size,
              height: size,
              borderRadius: 2,
              backgroundColor: color,
              pointerEvents: 'none',
            }}
          />
        );
      })}
    </Box>
  );
}

function InstituteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated } = useContext(AuthContext);
  const { track } = useAnalytics();

  const [institute, setInstitute] = useState(null);
  const [plans, setPlans] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leadOpen, setLeadOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [message, setMessage] = useState('');
  const [preferredBatchStart, setPreferredBatchStart] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [leadLoading, setLeadLoading] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState('');
  const [shareSnackbar, setShareSnackbar] = useState(false);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
    return `${baseUrl}${path}`;
  };

  const getGalleryImages = () => {
    if (institute?.gallery?.length > 0) {
      return institute.gallery.map(getImageUrl);
    }
    return FALLBACK_IMAGES;
  };

  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
      /youtube\.com\/shorts\/([^&\s?]+)/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const getYouTubeThumbnail = (url) => {
    const videoId = getYouTubeVideoId(url);
    if (!videoId) return null;
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  };

  const formatDate = (date) => {
    if (!date) return null;
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  // ── Lightbox handler ─────────────────────────────────────────────────────────

  const openLightbox = (images, index) => {
    setLightboxImages(images);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  // ── Share handler ────────────────────────────────────────────────────────────

  const handleShare = async () => {
    if (!institute) return;
    const shareUrl = `${window.location.origin}/institutes/${id}`;
    const shareText = `Check out ${institute.name} on BookMyLibrary!`;

    if (navigator.share) {
      try {
        await navigator.share({ title: institute.name, text: shareText, url: shareUrl });
      } catch (err) {
        if (err.name !== 'AbortError') {
          await navigator.clipboard.writeText(shareUrl);
          setShareSnackbar(true);
        }
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      setShareSnackbar(true);
    }
  };

  // ── Data fetching ─────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchInstituteDetails();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchInstituteDetails = async () => {
    try {
      setLoading(true);
      const [instituteRes, plansRes, batchesRes] = await Promise.all([
        api.get(`/institutes/${id}`),
        api.get(`/institutes/${id}/plans`),
        api.get(`/institutes/${id}/batches`),
      ]);
      setInstitute(instituteRes.data.data.institute);
      setPlans(plansRes.data.data.plans || []);
      setBatches(batchesRes.data.data.batches || []);
    } catch (err) {
      setError('Failed to load institute details');
    } finally {
      setLoading(false);
    }
  };

  // ── Lead form ─────────────────────────────────────────────────────────────────

  const handleShowInterest = (plan = null) => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/institutes/${id}` } });
      return;
    }
    setSelectedPlan(plan);
    setMessage('');
    setPreferredBatchStart('');
    setError('');
    setSuccess('');
    setLeadOpen(true);
  };

  const confirmInterest = async () => {
    try {
      setLeadLoading(true);
      setError('');

      const payload = { institute_id: id };
      if (selectedPlan?.id) payload.plan_id = selectedPlan.id;
      if (message?.trim()) payload.message = message;
      if (preferredBatchStart?.trim()) payload.preferred_batch_start = preferredBatchStart;

      await api.post('/institutes/leads', payload);
      track(EVT_INSTITUTE_LEAD_SUBMIT, { instituteId: id });

      setSuccess('Interest registered successfully! The institute will contact you soon.');
      setLeadOpen(false);

      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 1500);
      setTimeout(() => navigate('/student/institute-leads'), 2000);
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.details?.length) {
        setError(errorData.details.map((e) => e.message || e).join(', '));
      } else if (errorData?.errors?.length) {
        setError(errorData.errors.map((e) => e.msg).join(', '));
      } else {
        setError(errorData?.message || 'Failed to register interest');
      }
    } finally {
      setLeadLoading(false);
    }
  };

  const handlePlayVideo = (videoUrl) => {
    setCurrentVideoUrl(videoUrl);
    setVideoDialogOpen(true);
  };

  // Lead form — shared between BottomSheet and Dialog
  const LeadFormContent = () => (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TextField
        fullWidth
        multiline
        rows={3}
        label="Message (Optional)"
        placeholder="Tell us about your interests, background, or any questions..."
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        type="date"
        label="Preferred Batch Start Date (Optional)"
        value={preferredBatchStart}
        onChange={(e) => setPreferredBatchStart(e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{ mb: 2 }}
      />
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          onClick={() => setLeadOpen(false)}
          disabled={leadLoading}
          variant="text"
          sx={{ flex: 1 }}
        >
          Cancel
        </Button>
        <Button
          onClick={confirmInterest}
          variant="contained"
          disabled={leadLoading}
          sx={{ flex: 2 }}
        >
          {leadLoading ? 'Submitting...' : 'Submit Interest'}
        </Button>
      </Box>
    </>
  );

  // ── Loading state ─────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh' }}>
        <Container maxWidth="md" sx={{ py: 2 }}>
          <Skeleton variant="rectangular" height={300} sx={{ borderRadius: RADIUS.lg, mb: 2 }} />
          <Skeleton variant="text" height={50} width="70%" />
          <Skeleton variant="text" height={30} width="50%" />
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={6} key={i}>
                <Skeleton variant="rectangular" height={80} sx={{ borderRadius: RADIUS.md }} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  }

  if (!institute) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="h6">Institute not found</Typography>
      </Box>
    );
  }

  const currentYear = new Date().getFullYear();

  return (
    <Box sx={{ minHeight: '100vh', pb: { xs: 10, md: 0 } }}>
      {/* Desktop nav */}
      <DesktopNav />

      {/* Confetti burst */}
      <AnimatePresence>
        {showConfetti && <ConfettiBurst active />}
      </AnimatePresence>

      {/* Sticky header — mobile only */}
      <Box
        sx={{
          display: { xs: 'block', md: 'none' },
          position: 'sticky',
          top: 0,
          zIndex: 100,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="md">
          <Box display="flex" alignItems="center" justifyContent="space-between" py={1.5}>
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton
                onClick={() => navigate(isAuthenticated ? '/student/institutes' : '/institutes')}
                size="small"
                aria-label="Go back"
              >
                <ArrowBack />
              </IconButton>
              <Typography
                variant="h6"
                fontWeight={700}
                noWrap
                sx={{ maxWidth: 200 }}
              >
                {institute.name}
              </Typography>
            </Box>
            <Box display="flex" gap={0.5}>
              <IconButton size="small" aria-label="Add to favourites">
                <FavoriteBorder />
              </IconButton>
              <IconButton size="small" onClick={handleShare} aria-label="Share">
                <Share />
              </IconButton>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Hero carousel */}
      <HeroCarousel
        images={getGalleryImages()}
        alt={institute.name}
        onImageClick={(idx) => openLightbox(getGalleryImages(), idx)}
      />

      <Container maxWidth="md" sx={{ mt: -4, position: 'relative', zIndex: 10 }}>
        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2, borderRadius: RADIUS.md }}
            onClose={() => setSuccess('')}
          >
            {success}
          </Alert>
        )}

        {/* Main Info Card */}
        <MotionCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          sx={{ borderRadius: RADIUS.lg, mb: 3, overflow: 'visible' }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="start" gap={2} mb={2}>
              {/* Avatar — Phase 4: size bumped to 72×72 */}
              <Avatar
                sx={{
                  width: 72,
                  height: 72,
                  bgcolor: 'primary.main',
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  mt: -5,
                  border: '4px solid',
                  borderColor: 'background.paper',
                  boxShadow: 2,
                }}
              >
                {institute.name.charAt(0)}
              </Avatar>
              <Box flex={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="h5" fontWeight={700}>
                    {institute.name}
                  </Typography>
                  {institute.rating >= 4 && <Verified color="primary" fontSize="small" />}
                </Box>
                {institute.city && (
                  <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                    <LocationOn fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {institute.city}
                    </Typography>
                  </Box>
                )}

                {/* Phase 3: Manager attribution */}
                {institute.manager && (
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    mt={1}
                    flexWrap="wrap"
                  >
                    {institute.manager.name && (
                      <>
                        <Avatar
                          sx={{ width: 24, height: 24, fontSize: '0.65rem', bgcolor: 'primary.soft' }}
                        >
                          {institute.manager.name.charAt(0)}
                        </Avatar>
                        <Typography variant="caption" color="text.secondary">
                          Managed by{' '}
                          <Typography
                            component="span"
                            variant="caption"
                            fontWeight={700}
                            color="text.primary"
                          >
                            {institute.manager.name}
                          </Typography>
                        </Typography>
                      </>
                    )}
                    <Chip
                      size="small"
                      label="Verified Institute"
                      color="primary"
                      variant="outlined"
                      icon={<Verified sx={{ fontSize: 12 }} />}
                    />
                  </Box>
                )}
              </Box>
            </Box>

            {/* Phase 4: 4-tile stat row — always rendered */}
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              {/* Rating */}
              <Grid item xs={6} sm={3}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: RADIUS.md,
                    bgcolor:
                      institute.rating > 0
                        ? alpha(theme.palette.warning.main, 0.1)
                        : alpha(theme.palette.warning.main, 0.05),
                    textAlign: 'center',
                  }}
                >
                  <Star
                    sx={{
                      color: 'warning.main',
                      fontSize: 28,
                      opacity: institute.rating > 0 ? 1 : 0.5,
                    }}
                  />
                  <Typography variant="h6" fontWeight={700}>
                    {institute.rating > 0 ? `${Number(institute.rating).toFixed(1)} ★` : 'New'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Rating
                  </Typography>
                </Paper>
              </Grid>

              {/* Success rate */}
              <Grid item xs={6} sm={3}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: RADIUS.md,
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    textAlign: 'center',
                  }}
                >
                  <TrendingUp sx={{ color: 'success.main', fontSize: 28 }} />
                  <Typography variant="h6" fontWeight={700}>
                    {institute.success_rate > 0 ? `${institute.success_rate}%` : '—'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Success Rate
                  </Typography>
                </Paper>
              </Grid>

              {/* Students */}
              <Grid item xs={6} sm={3}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: RADIUS.md,
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    textAlign: 'center',
                  }}
                >
                  <Groups sx={{ color: 'info.main', fontSize: 28 }} />
                  <Typography variant="h6" fontWeight={700}>
                    {institute.total_students > 0
                      ? `${institute.total_students}+`
                      : 'Just opened'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Students
                  </Typography>
                </Paper>
              </Grid>

              {/* Years active */}
              <Grid item xs={6} sm={3}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: RADIUS.md,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    textAlign: 'center',
                  }}
                >
                  <Business sx={{ color: 'primary.main', fontSize: 28 }} />
                  <Typography variant="h6" fontWeight={700}>
                    {institute.established_year
                      ? `${currentYear - institute.established_year}y`
                      : 'New'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Years Active
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Established year caption */}
            {institute.established_year && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 2 }}
              >
                Established {institute.established_year}
              </Typography>
            )}

            {/* Courses */}
            {institute.courses_offered?.length > 0 && (
              <Box mb={2}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Courses Offered
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {institute.courses_offered.map((course, idx) => (
                    <Chip
                      key={idx}
                      label={course}
                      size="small"
                      color="primary"
                      variant="outlined"
                      icon={<Category fontSize="small" />}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Description */}
            {institute.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.7, mb: 2 }}
              >
                {institute.description}
              </Typography>
            )}

            {/* Address */}
            {institute.address && (
              <Box
                display="flex"
                gap={1}
                p={1.5}
                bgcolor="background.subtle"
                sx={{ borderRadius: RADIUS.md }}
              >
                <Place color="action" />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Address
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {institute.address}
                    {institute.city ? `, ${institute.city}` : ''}
                  </Typography>
                </Box>
              </Box>
            )}
          </CardContent>
        </MotionCard>

        {/* WhatsApp Enquiry */}
        <Button
          component="a"
          href={`https://wa.me/${
            process.env.REACT_APP_WHATSAPP_NUMBER || '918595675155'
          }?text=${encodeURIComponent(
            `Hi! I am interested in ${institute.name}. Please share more details.`
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          fullWidth
          variant="contained"
          startIcon={<WhatsApp />}
          sx={{
            mb: 3,
            py: 1.5,
            borderRadius: RADIUS.lg,
            bgcolor: '#25D366',
            color: 'common.white',
            fontWeight: 600,
            fontSize: '1rem',
            '&:hover': { bgcolor: '#128C7E' },
          }}
        >
          Enquire on WhatsApp
        </Button>

        {/* Facilities */}
        {institute.facilities?.length > 0 && (
          <Card sx={{ borderRadius: RADIUS.lg, mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Facilities &amp; Amenities
              </Typography>
              <Grid container spacing={1}>
                {institute.facilities.map((facility, idx) => (
                  <Grid item xs={6} sm={4} key={idx}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      p={1}
                      bgcolor="background.subtle"
                      sx={{ borderRadius: RADIUS.sm }}
                    >
                      <Check fontSize="small" color="success" />
                      <Typography variant="body2">{facility}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Contact */}
        {(institute.contact_phone || institute.contact_email || institute.website) && (
          <Card sx={{ borderRadius: RADIUS.lg, mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Contact Information
              </Typography>
              <Grid container spacing={2}>
                {institute.contact_phone && (
                  <Grid item xs={12} sm={4}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Avatar
                        sx={{
                          bgcolor: 'primary.soft',
                          color: 'primary.main',
                          width: 40,
                          height: 40,
                        }}
                      >
                        <Phone fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Phone
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {institute.contact_phone}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
                {institute.contact_email && (
                  <Grid item xs={12} sm={4}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Avatar
                        sx={{
                          bgcolor: alpha(theme.palette.info.main, 0.12),
                          color: 'info.main',
                          width: 40,
                          height: 40,
                        }}
                      >
                        <Email fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Email
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ wordBreak: 'break-all' }}
                        >
                          {institute.contact_email}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
                {institute.website && (
                  <Grid item xs={12} sm={4}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Avatar
                        sx={{
                          bgcolor: alpha(theme.palette.success.main, 0.12),
                          color: 'success.main',
                          width: 40,
                          height: 40,
                        }}
                      >
                        <Language fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Website
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          component="a"
                          href={institute.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: 'primary.main', textDecoration: 'none' }}
                        >
                          Visit Website
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Programs & Pricing */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom mb={0.5}>
            Programs &amp; Courses
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Pick a course track that fits your goals
          </Typography>
          <PlanShelf plans={plans} onShowInterest={handleShowInterest} />
        </Box>

        {/* Upcoming Batches */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom mb={0.5}>
            Upcoming Batches
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Live and upcoming start dates
          </Typography>
          <BatchShelf
            batches={batches}
            onShowInterest={handleShowInterest}
            onPlayVideo={handlePlayVideo}
            onOpenLightbox={openLightbox}
            getImageUrl={getImageUrl}
            formatDate={formatDate}
            getYouTubeThumbnail={getYouTubeThumbnail}
            getYouTubeVideoId={getYouTubeVideoId}
          />
        </Box>

        {/* General Interest CTA — inline, above bottom nav */}
        <Button
          fullWidth
          variant="outlined"
          size="large"
          onClick={() => handleShowInterest()}
          sx={{ borderRadius: RADIUS.lg, py: 1.5, mb: 2 }}
        >
          Express General Interest
        </Button>
      </Container>

      {/* Lightbox */}
      <Lightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={lightboxImages}
        index={lightboxIndex}
        onIndexChange={setLightboxIndex}
        alt={institute.name}
      />

      {/* Lead form — BottomSheet on mobile, Dialog on desktop */}
      {isMobile ? (
        <BottomSheet
          open={leadOpen}
          onClose={() => !leadLoading && setLeadOpen(false)}
          title={selectedPlan ? `Interest: ${selectedPlan.name}` : 'Express Interest'}
        >
          <LeadFormContent />
        </BottomSheet>
      ) : (
        <Dialog
          open={leadOpen}
          onClose={() => !leadLoading && setLeadOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: RADIUS.lg } }}
        >
          <DialogTitle>
            Express Interest
            {selectedPlan && (
              <Typography variant="caption" display="block" color="text.secondary">
                Program: {selectedPlan.name}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1 }}>
              <LeadFormContent />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }} />
        </Dialog>
      )}

      {/* Video Player Dialog */}
      <Dialog
        open={videoDialogOpen}
        onClose={() => setVideoDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'common.black',
            borderRadius: RADIUS.lg,
            overflow: 'hidden',
            m: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: 'common.white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 1.5,
            px: 2,
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <YouTube sx={{ color: 'error.main' }} />
            <Typography variant="subtitle1" fontWeight={600}>
              Demo Video
            </Typography>
          </Box>
          <IconButton
            onClick={() => setVideoDialogOpen(false)}
            sx={{ color: 'common.white' }}
            aria-label="Close video"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <Box
          sx={{
            width: '100%',
            aspectRatio: '16/9',
            position: 'relative',
            bgcolor: 'common.black',
          }}
        >
          {currentVideoUrl && getYouTubeVideoId(currentVideoUrl) ? (
            <iframe
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              src={`https://www.youtube.com/embed/${getYouTubeVideoId(currentVideoUrl)}?autoplay=1&rel=0`}
              title="Demo Video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'common.white',
              }}
            >
              <Typography>Loading video...</Typography>
            </Box>
          )}
        </Box>
      </Dialog>

      {/* Share snackbar */}
      <Snackbar
        open={shareSnackbar}
        autoHideDuration={2000}
        onClose={() => setShareSnackbar(false)}
        message="Link copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Student bottom nav */}
      {isAuthenticated && <StudentBottomNav />}
    </Box>
  );
}

export default InstituteDetail;
